/**
 * AccountDetails — personal account surface (distinct from workspace Settings).
 * Ported from the prototype's screens-account.jsx and wired to the real account
 * contract: getAccountDetails + the profile/preferences/password/
 * session/close mutations from @v0y4ge/api-client. (Connected-identities and
 * workspace-access-request affordances removed 2026-07-21 by operator decision:
 * identity is tenant/SSO-dictated, not user-brought; workspace access is
 * read-only from the identity system, never requestable.)
 *
 * CD owns the UI; the data layer returns exactly the fields the screen reads
 * (see briefings/account.md). Reads are defensive (Record<string, any> + ??)
 * so partial/loading responses never crash the render — the same convention
 * used across the shell (access.ts, SettingsGeneral).
 *
 * i18n: fully keyed, INCLUDING the ICU sites (password policy / last-changed,
 * workspace meta counts, close-account impact bullets) — these now consume the
 * shipped REQ-048 ICU catalog entries (account.details.passwordPolicy /
 * workspaceEventCount / closeImpact* family, landed in the 2026-07-22 catalog).
 * The legacy fragment keys they replaced are retired catalog-side (Voyage owns
 * @v0y4ge/i18n-catalog); full history: REQ-048 in
 * cd-kit-composed/CLAUDE-CODE-REQUESTS.md.
 */
import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import {
  getAccountDetails, updateAccountProfile, updateAccountPreferences,
  changeAccountPassword,
  revokeAccountSession, revokeOtherAccountSessions, closeAccount,
  listAccountPasskeys, registerPasskey, renameAccountPasskey, deleteAccountPasskey,
  isPasskeySupported,
} from "@v0y4ge/api-client";
import { Avatar, Btn, Card, Checkbox, Disclosure, Dropdown, Icon, Input, OtpInput, Pill, SideNav, Skeleton, T, Toggle, alpha, tx, supportedTimeZoneIds } from "@v0y4ge/jrni-ui";
import { useMyStaffProfile } from "../staff/useStaffTimezone";
import { Screen } from "../../shell/chrome";
import { Drawer, Modal } from "../../shell/Overlay";
import { useNav } from "../../shell/router";
import { switchWorkspace, getActiveOrganizationId } from "../../shell/access";
import { toast } from "../../shell/toast";
import { availableLocales, currentLocale, localeDisplayName, persistLocalePreference, refreshTranslationCatalog, setAppLocale, useLocaleVersion, useT } from "../../lib/i18n";
import { friendlyDateShort } from "../../lib/datetime";
import { ASVS_MAX_LENGTH, evaluatePassword, type RuleId } from "./password-policy";

const ACCT = { baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never;
type Any = Record<string, any>;

/* Local labelled controls. One field shell (label + optional helper text) wraps
 * both the text inputs and the canonical jrni-ui <Dropdown>, so every control on
 * this page shares one label treatment. Native <select> is retired here — the
 * design system's Dropdown is what the rest of the dashboard uses. */
function FieldShell({ label, helper, error, children }: { label: string; helper?: string; error?: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: T.space[1] }}>
      <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.n2 }}>{label}</div>
      {children}
      {error ? <div role="alert" style={{ fontSize: T.text.label.fontSize, color: T.red1, lineHeight: 1.45, display: "flex", alignItems: "center", gap: T.space[1] }}><Icon.warn size={12} />{error}</div>
        : helper ? <div style={{ fontSize: T.text.label.fontSize, color: T.soft, lineHeight: 1.45 }}>{helper}</div> : null}
    </div>
  );
}
function LabelInput({ label, helper, error, value, onChange, onBlur, leading, type, placeholder, readOnly }: { label: string; helper?: string; error?: string; value: string; onChange?: (v: string) => void; onBlur?: () => void; leading?: ReactNode; type?: string; placeholder?: string; readOnly?: boolean }) {
  return (
    <FieldShell label={label} helper={helper} error={error}>
      {/* Migrated to jrni-ui <Input> (component-adoption 2026-08-06). FieldShell
       * keeps the label/error so text fields stay consistent with this page's
       * Dropdowns; the error TEXT renders in FieldShell, so `error` is not passed
       * to Input (which would render its own duplicate error line). */}
      <Input value={value} type={type as never} placeholder={placeholder} readOnly={readOnly} onBlur={onBlur} leading={leading} onChange={(e: ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value)} />
    </FieldShell>
  );
}
/** "America/Chicago (GMT-5)": the IANA name is the value, the offset is only shown. */
function tzLabel(iana: string): string {
  try {
    const off = new Intl.DateTimeFormat("en-US", { timeZone: iana, timeZoneName: "shortOffset" }).formatToParts(new Date()).find((x) => x.type === "timeZoneName")?.value;
    return off ? `${iana} (${off})` : iana;
  } catch { return iana; }
}
function LabelDropdown({ label, helper, value, options, pairs, onChange, placeholder }: { label: string; helper?: string; value: string; options?: string[]; pairs?: { v: string; l: string }[]; onChange?: (v: string) => void; placeholder?: string }) {
  const opts: { v: string; l: string }[] = pairs ?? (options ?? []).map((o) => ({ v: o, l: o }));
  const withValue = value && !opts.some((o) => o.v === value) ? [{ v: value, l: value }, ...opts] : opts;
  return (
    <FieldShell label={label} helper={helper}>
      <Dropdown
        options={withValue.map((o) => ({ value: o.v, label: o.l }))}
        value={value || null}
        placeholder={placeholder}
        aria-label={label}
        onChange={(v) => onChange?.(v)}
        style={{ gap: 0 }}
      />
    </FieldShell>
  );
}
/* Section labels resolve at render time via t(k, l). All shipped in the 33-locale
 * catalog except account.details.notificationPreferences (new 2026-07-21 rename
 * from "Preferences" — flagged for the catalog in REQ-048). */
const SECTIONS = [
  { id: "profile", k: "account.details.profile", l: "Profile", i: <Icon.user size={14} /> },
  { id: "security", k: "account.details.security", l: "Security", i: <Icon.settings size={14} /> },
  { id: "sessions", k: "account.details.activeSessions", l: "Active sessions", i: <Icon.eye size={14} /> },
  { id: "prefs", k: "account.details.notificationPreferences", l: "Notification preferences", i: <Icon.bell size={14} /> },
  { id: "workspaces", k: "account.details.workspaces", l: "Workspaces", i: <Icon.network size={14} /> },
  { id: "danger", k: "account.details.closeAccount", l: "Close account", i: <Icon.warn size={14} />, danger: true },
] as const;

export default function AccountDetails() {
  const t = useT();
  const nav = useNav();
  const [section, setSection] = useState<string>("profile");
  const [acct, setAcct] = useState<Any | null>(null);
  const reload = () => {
    if (typeof getAccountDetails !== "function") return;
    (getAccountDetails as (o: never) => Promise<unknown>)(ACCT)
      .then((r: unknown) => { const raw = r as Any; setAcct(raw.account ?? raw.data ?? raw); })
      .catch((e: unknown) => console.warn("getAccountDetails failed:", e));
  };
  useEffect(() => { reload(); }, []);

  return (
    <Screen rail="settings">
      <div style={{ padding: `${T.space[5]} ${T.space[6]} 0` }}>
        {/* ui-raw: hand-rolled back-link + trail. Breadcrumb (jrni-ui) applies
         * ONE separator between every item, but this bar mixes two different
         * separators — a fixed 12px-tall divider bar between Back/Account and
         * an Icon.chev between Account/Details — which Breadcrumb can't
         * express. The divider bar also can't be swapped 1:1 for jrni-ui's
         * <Divider orientation="vertical">: that stretches to its flex
         * container's height, not this bar's fixed 12px. Kept raw. */}
        <div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft, display: "flex", alignItems: "center", gap: T.space[2] }}>
          <span style={{ cursor: "pointer" }} onClick={() => nav.back()}>{t("account.details.back", "← Back")}</span>
          <span style={{ width: 1, height: 12, background: T.border, margin: `0 ${T.space[2]}` }} />
          <span>{t("account.details.account", "Account")}</span><Icon.chev size={10} dir="right" /><span style={{ color: T.n1 }}>{t("account.details.details", "Details")}</span>
        </div>
        <div style={{ marginTop: T.space[2] }}>
          <h1 style={{ ...tx.h2, fontWeight: 700 }}>{t("account.details.accountDetails", "Account details")}</h1>
          <p style={tx.help}>{t("account.details.yourProfileSecurityAndPersonalPreferencesAcrossEveryWorkspace", "Your profile, security, and personal preferences across every workspace.")}</p>
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", minHeight: 0, padding: `${T.space[4]} ${T.space[6]} ${T.space[6]}`, gap: T.space[4] }}>
        <SideNav<string>
          value={section}
          onChange={setSection}
          items={SECTIONS.map((s) => ({ key: s.id, label: t(s.k, s.l), icon: s.i, danger: (s as { danger?: boolean }).danger }))}
        />

        <div style={{ flex: 1, minWidth: 0, overflow: "auto", display: "flex", flexDirection: "column", gap: T.space[4] }}>
          {section === "profile" ? <ProfileSection acct={acct} onSaved={reload} /> : null}
          {section === "security" ? <SecuritySection acct={acct} onChanged={reload} /> : null}
          {section === "sessions" ? <SessionsSection acct={acct} onChanged={reload} /> : null}
          {section === "prefs" ? <PrefsSection acct={acct} onSaved={reload} /> : null}
          {section === "workspaces" ? <WorkspacesSection acct={acct} /> : null}
          {section === "danger" ? <DangerSection acct={acct} /> : null}
        </div>
      </div>
    </Screen>
  );
}

/** Loose mobile-number check: optional leading +, then 7–15 digits (spaces,
 * (), -, . are allowed as formatting). Empty passes — the field is optional. */
function isValidPhone(v: string): boolean {
  const s = v.trim();
  if (!s) return true;
  const digits = s.replace(/[^0-9]/g, "");
  return /^\+?[0-9()\s.-]+$/.test(s) && digits.length >= 7 && digits.length <= 15;
}

function ProfileSection({ acct, onSaved }: { acct: Any | null; onSaved: () => void }) {
  const t = useT();
  const p: Any = acct?.profile ?? {};
  const [first, setFirst] = useState(""); const [last, setLast] = useState("");
  const [phone, setPhone] = useState(""); const [title, setTitle] = useState("");
  const [phoneErr, setPhoneErr] = useState<string | undefined>(undefined);
  /* IANA names only (CC 0017 §1.1): the account contract refuses anything else.
     "" = Organisation default. A stored legacy label ("… (GMT-5)") is read as
     its IANA part. */
  const [tz, setTz] = useState("");
  const staff = useMyStaffProfile();
  const [dateFmt, setDateFmt] = useState("DD MMM YYYY · 8 May 2026");
  useEffect(() => {
    if (!acct) return;
    setFirst(p.firstName ?? ""); setLast(p.lastName ?? "");
    setPhone(p.phoneNumber ?? ""); setTitle(p.displayTitle ?? "");
    const stored = String(p.locale?.timeZone || "").replace(/\s*\(GMT[^)]*\)\s*$/, "").trim();
    setTz(stored || String(staff.profile?.timezone || ""));
    if (p.locale?.dateFormat) setDateFmt(p.locale.dateFormat);
  }, [acct]);
  /* Language lives on the USER RECORD (operator decision 2026-07-21): the app
   * locale IS this profile field. Picking one applies session-wide instantly
   * (setAppLocale) and persists via the typed setUserLocale op — the same
   * mechanism the retired avatar-menu switcher used before it moved here.
   * Read-side seeding: REQ-048(E) — getAccountDetails().profile.locale.language
   * should echo the persona's saved tag. */
  useLocaleVersion();
  const localeTag = currentLocale();
  const pickLocale = (tag: string) => {
    if (!tag || tag === localeTag) return;
    setAppLocale(tag);
    void refreshTranslationCatalog(getActiveOrganizationId());
    void persistLocalePreference(tag).then((r) => { if (r === "failed") toast({ tone: "danger", title: t("chrome.userMenu.couldnTSaveYourLanguagePreference", "Couldn't save your language preference"), subtitle: t("chrome.userMenu.itSAppliedForThisSessionPickItAgainToRetryThe", "It's applied for this session — pick it again to retry the save.") }); });
  };
  const avatar: Any = p.avatar ?? {};

  /* Email is NOT patchable: AccountProfilePatchInput has no `email` key (it is
   * a plain z.object, so an added key is silently stripped) and no change-email
   * op exists in OPERATIONS_REGISTRY. The field therefore stays read-only
   * rather than accepting edits it would discard — REQ-093. */
  const save = () => {
    if (!isValidPhone(phone)) { setPhoneErr(t("account.details.phoneInvalid", "Enter a valid phone number — 7–15 digits, optional +.")); return; }
    (updateAccountProfile as (i: Any, o: never) => Promise<unknown>)(
      { firstName: first, lastName: last, displayTitle: title, phoneNumber: phone, locale: { timeZone: tz, dateFormat: dateFmt } }, ACCT,
    )
      /* A member with a staff record: the same zone moves their My Day, which is
         drawn from the staff record (setMyStaffTimezone). Clearing sends null there. */
      .then(() => (staff.profile && String(staff.profile.timezone || "") !== tz ? staff.setTimezone(tz || null) : null))
      .then(() => { toast({ tone: "success", title: t("account.details.profileSaved", "Profile saved") }); onSaved(); })
      .catch((e: unknown) => { console.warn("updateAccountProfile failed:", e); toast({ tone: "danger", title: t("account.details.couldnTSaveProfile", "Couldn't save profile") }); });
  };

  return (
    <>
      <Card style={{ padding: T.space[6] }}>
        <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("account.details.profile", "Profile")}</h3>
        <div style={{ display: "flex", gap: T.space[4], marginTop: T.space[3], alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: T.space[2] }}>
            <Avatar initials={avatar.initials ?? "?"} size={80} color={avatar.color ?? T.p1} />
            <Btn variant="ghost" size="sm" icon={<Icon.upload size={12} />}>{t("account.details.change", "Change")}</Btn>
          </div>
          <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: T.space[3], alignItems: "start" }}>
            <LabelInput label={t("account.details.firstName", "First name")} value={first} onChange={setFirst} placeholder={t("account.details.firstNamePlaceholder", "e.g. Maya")} />
            <LabelInput label={t("account.details.lastName", "Last name")} value={last} onChange={setLast} placeholder={t("account.details.lastNamePlaceholder", "e.g. Okonkwo")} />
            <LabelInput label={t("account.details.emailAddress", "Email address")} value={p.email ?? ""} leading={<Icon.mail size={14} />} readOnly
              helper={t("account.details.emailHelp", "Used to sign in and to receive system notifications. Your identity provider owns this address — ask an admin to change it.")} />
            <LabelInput label={t("account.details.mobileNumber", "Mobile number")} value={phone}
              onChange={(v) => { setPhone(v); if (phoneErr) setPhoneErr(undefined); }}
              onBlur={() => setPhoneErr(isValidPhone(phone) ? undefined : t("account.details.phoneInvalid", "Enter a valid phone number — 7–15 digits, optional +."))}
              error={phoneErr} type="tel" leading={<Icon.phone size={14} />}
              placeholder={t("account.details.phonePlaceholder", "+44 7700 900123")}
              helper={t("account.details.phoneHelp", "For event-day contact only. Include the country code.")} />
            <LabelInput label={t("account.details.jobTitle", "Job title")} value={title} onChange={setTitle}
              placeholder={t("account.details.jobTitlePlaceholder", "e.g. Head of Events")}
              helper={t("account.details.jobTitleHelp", "Shown beside your name to teammates across every workspace.")} />
            <LabelDropdown label={t("account.details.timeZone", "Time zone")} value={tz}
              pairs={[{ v: "", l: t("account.details.organisationDefault", "Organisation default") }, ...supportedTimeZoneIds().map((z) => ({ v: z, l: tzLabel(z) }))]} onChange={setTz}
              helper={t("account.details.timeZoneHelp", "Every event time and report in Studio is shown in this zone.")} />
            <LabelDropdown label={t("account.details.displayLanguage", "Display language")} value={localeTag}
              pairs={availableLocales().map((tag) => ({ v: tag, l: `${localeDisplayName(tag)} (${tag})` }))} onChange={pickLocale}
              helper={t("account.details.languageHelp", "Applies to Studio for you only — it doesn't change what attendees see.")} />
            <LabelDropdown label={t("account.details.dateFormat", "Date format")} value={dateFmt}
              options={["DD MMM YYYY · 8 May 2026", "MM/DD/YYYY · 05/08/2026", "YYYY-MM-DD · 2026-05-08"]} onChange={setDateFmt}
              helper={t("account.details.dateFormatHelp", "How dates are written across Studio, including reports and exports.")} />
          </div>
        </div>
      </Card>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: T.space[2] }}>
        <Btn variant="secondary" onClick={() => onSaved()}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn onClick={save}>{t("account.details.saveChanges", "Save changes")}</Btn>
      </div>
    </>
  );
}

function SecuritySection({ acct, onChanged }: { acct: Any | null; onChanged: () => void }) {
  const t = useT();
  /* Same null-vs-[] rule as Sessions/Workspaces: while the account read is in
   * flight there is no MFA state to report, so the card must not print "Off"
   * and "No second factor yet" — a security claim it cannot yet make. */
  const loading = acct === null;
  const pw: Any = acct?.password ?? {};
  const prof: Any = acct?.profile ?? {};
  const mfa: Any = acct?.mfa ?? {};
  const methods: Any[] = Array.isArray(mfa.methods) ? mfa.methods : [];
  const [cur, setCur] = useState(""); const [next, setNext] = useState(""); const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const minLen = Math.max(12, pw.policy?.minLength ?? 12);
  // OWASP ASVS §2.1 evaluation, recomputed on every keystroke (2.1.8 meter).
  const evalRes = evaluatePassword(next, { minLength: minLen, email: prof.email, firstName: prof.firstName, lastName: prof.lastName, currentPassword: cur });
  const matches = confirm.length > 0 && confirm === next;
  const canSubmit = cur.length > 0 && evalRes.valid && matches;
  const [flow, setFlow] = useState<null | "enroll" | "codes">(null);
  const [removeSms, setRemoveSms] = useState<Any | null>(null);
  const changePw = () => {
    if (!canSubmit) { setTouched(true); return; }
    (changeAccountPassword as unknown as (i: Any, o: never) => Promise<unknown>)({ currentPassword: cur, newPassword: next, confirmPassword: confirm }, ACCT)
      .then(() => { toast({ tone: "success", title: t("account.details.passwordUpdated", "Password updated") }); setCur(""); setNext(""); setConfirm(""); onChanged(); })
      .catch((e: unknown) => { console.warn("changeAccountPassword failed:", e); toast({ tone: "danger", title: t("account.details.couldnTUpdatePassword", "Couldn't update password") }); });
  };
  const mfaIcon = (k: string) => k === "totp" ? <Icon.qr size={14} /> : k === "recovery_codes" ? <Icon.csv size={14} /> : k === "sms" ? <Icon.msg size={14} /> : <Icon.lock size={14} />;
  const totp = methods.filter((m) => m.type === "totp");
  const smsMethods = methods.filter((m) => m.type === "sms");
  const recovery = methods.find((m) => m.type === "recovery_codes");
  return (
    <>
      {/* Honest non-functional notice (operator 2026-07-21): this whole screen
       * is NOT wired to any authentication infrastructure — password + 2FA here
       * change nothing real. Mandatory per the no-fake-interactivity rule. */}
      {/* ui-raw: hand-rolled tonal notice. Banner is the closest jrni-ui match
       * (same icon+title+body shape) but its computed tone fill (alpha 9/28)
       * and title/body type (14px/13px) don't reproduce this notice's tuned
       * alpha(12/55) + 13.5px/12.5px scale without visible drift — kept raw. */}
      <div role="note" style={{ display: "flex", gap: T.space[2], alignItems: "flex-start", padding: T.space[3], borderRadius: T.radius.md, border: `1px solid ${alpha(T.orange1, 55)}`, background: alpha(T.orange1, 12) }}>
        <span style={{ color: T.orange1, display: "inline-flex", flexShrink: 0, marginTop: 1 /* optical */ }}><Icon.warn size={16} /></span>
        <div>
          <div style={{ fontSize: T.text.body.fontSize, fontWeight: 700, color: T.n1 }}>{t("account.details.securityNotWiredTitle", "Not connected to live authentication")}</div>
          <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.5, marginTop: T.space[1] }}>{t("account.details.securityNotWiredBody", "This screen is a design preview — password changes and two-factor settings here are not plugged into any authentication system. Nothing on this screen affects real credentials or sign-in.")}</div>
        </div>
      </div>
      <Card style={{ padding: T.space[6] }}>
        <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("account.details.password", "Password")}</h3>
        <p style={tx.help}>{pw.lastChangedAt ? t("account.details.passwordLastChanged", "Last changed {date}.", { date: new Date(pw.lastChangedAt).toLocaleDateString() }) : t("account.details.setAStrongPassword", "Set a strong password you don't use elsewhere.")}</p>
        <div style={{ marginTop: T.space[3], display: "flex", flexDirection: "column", gap: T.space[3], maxWidth: 480 }}>
          <LabelInput label={t("account.details.currentPassword", "Current password")} value={cur} onChange={setCur} type="password" />
          <LabelInput label={t("account.details.newPassword", "New password")} value={next} onChange={setNext} type="password"
            placeholder={t("account.details.newPasswordPlaceholder", "At least 12 characters")}
            helper={t("account.details.newPasswordHelp", "A long passphrase of unrelated words beats a short complicated one. Spaces and any symbol are allowed.")} />
          <PasswordComplexity minLen={minLen} value={next} evalRes={evalRes} />
          <LabelInput label={t("account.details.confirmNewPassword", "Confirm new password")} value={confirm} onChange={setConfirm} onBlur={() => setTouched(true)} type="password" placeholder={t("account.details.confirmPasswordPlaceholder", "Type it again")}
            error={(touched || confirm.length > 0) && confirm.length > 0 && !matches ? t("account.details.passwordsDontMatch", "Both passwords must match.") : undefined} />
          <div><Btn onClick={changePw} disabled={!canSubmit}>{t("account.details.updatePassword", "Update password")}</Btn></div>
        </div>
      </Card>
      {/* Two-factor. Industry practice, and deliberately opinionated: an
       * authenticator app (TOTP) or a passkey / hardware key are the only
       * factors offered. SMS is never offered as a choice — a factor that can be
       * redirected by a SIM swap or intercepted in transit isn't a second factor
       * worth the trust. Records that already carry an SMS factor are shown
       * honestly (we don't hide backend state) with a remove path. */}
      <Card style={{ padding: T.space[6] }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: T.space[4] }}>
          <div>
            <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("account.details.twoFactorAuthentication", "Two-factor authentication")}</h3>
            <p style={tx.help}>{t("account.details.mfaIntro", "A second step at sign-in, so a stolen password isn't enough on its own. Required for Admin roles.")}</p>
          </div>
          <Pill tone={loading ? "neutral" : totp.length > 0 ? "success" : "neutral"} dot>{loading ? t("account.details.mfaChecking", "Checking…") : totp.length > 0 ? t("account.details.enabled", "Enabled") : t("account.details.off", "Off")}</Pill>
        </div>

        {loading ? (
          <div style={{ marginTop: T.space[3], display: "flex", flexDirection: "column", gap: T.space[2] }} aria-busy>
            <Skeleton variant="rect" height={58} />
            <Skeleton variant="text" width="42%" height={11} />
          </div>
        ) : methods.length === 0 ? (
          /* ui-raw: hand-rolled empty state — InlineEmptyState is the closest
           * jrni-ui match, but its description `maxWidth` (320) is hardcoded
           * (not style-overridable) versus this block's 420px, and its title/
           * body type + padding scale differ; kept raw to preserve pixels. */
          <div style={{ marginTop: T.space[3], padding: T.space[6], border: `1px dashed ${T.n4}`, borderRadius: T.radius.md, textAlign: "center" }}>
            <div style={{ fontSize: T.text.body.fontSize, fontWeight: 600, color: T.n1 }}>{t("account.details.mfaEmptyTitle", "No second factor yet")}</div>
            <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.5, marginTop: T.space[1], maxWidth: 420, margin: `${T.space[1]} auto 0` }}>{t("account.details.mfaEmptyBody", "Set up an authenticator app on your phone. It takes about a minute and works offline.")}</div>
            <div style={{ marginTop: T.space[3] }}><Btn icon={<Icon.qr size={12} />} onClick={() => setFlow("enroll")}>{t("account.details.setUpAuthenticator", "Set up authenticator app")}</Btn></div>
          </div>
        ) : (
          <div style={{ marginTop: T.space[3], display: "flex", flexDirection: "column", gap: T.space[3] }}>
            {/* ui-raw: same reasoning as the Sessions/Workspaces rows below —
             * multi-line meta plus an inline Pill within the title line,
             * PLUS a "weak" highlighted border+background variant MediaRow
             * has no slot for; adopting MediaRow would clip/restyle this.
             * Kept raw. */}
            {[...totp, ...(recovery ? [recovery] : []), ...smsMethods].map((m) => {
              const weak = m.type === "sms";
              return (
                <div key={m.id} style={{ padding: T.space[3], border: `1px solid ${weak ? alpha(T.orange1, 55) : T.border}`, background: weak ? T.orange2 : T.white, borderRadius: T.radius.control, display: "flex", gap: T.space[3], alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ width: 32, height: 32, borderRadius: T.radius.control, background: weak ? T.white : T.p3, color: weak ? T.orange1 : T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{mfaIcon(m.type)}</span>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.n1, display: "flex", alignItems: "center", gap: T.space[2], flexWrap: "wrap" }}>
                      {m.label}
                      {weak ? <Pill tone="warn">{t("account.details.mfaNotRecommended", "Not recommended")}</Pill> : null}
                    </div>
                    <div style={{ fontSize: T.text.label.fontSize, color: weak ? T.orange1 : T.soft, lineHeight: 1.45 }}>
                      {weak ? t("account.details.mfaSmsWarning", "Text messages can be redirected with a SIM swap. Remove this and use your authenticator app instead.") : m.summary}
                    </div>
                    {!weak && (m.lastUsedAt || m.addedAt) ? (
                      <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[1] }}>
                        {m.lastUsedAt ? t("account.details.text", "{arg1} {arg2}", { arg1: t("account.details.mfaLastUsed", "Last used"), arg2: new Date(m.lastUsedAt).toLocaleDateString() }) : t("account.details.text", "{arg1} {arg2}", { arg1: t("account.details.mfaAdded", "Added"), arg2: new Date(m.addedAt).toLocaleDateString() })}
                      </div>
                    ) : null}
                  </div>
                  {weak
                    ? <Btn variant="secondary" size="sm" danger icon={<Icon.trash size={12} />} onClick={() => setRemoveSms(m)}>{t("common.actions.remove", "Remove")}</Btn>
                    : m.type === "recovery_codes"
                      ? <Btn variant="secondary" size="sm" onClick={() => setFlow("codes")}>{t("account.details.mfaViewCodes", "View codes")}</Btn>
                      : <Btn variant="ghost" size="sm">{t("common.actions.manage", "Manage")}</Btn>}
                </div>
              );
            })}
            <div style={{ display: "flex", gap: T.space[2], flexWrap: "wrap", marginTop: T.space[1] }}>
              <Btn variant="secondary" size="sm" icon={<Icon.qr size={12} />} onClick={() => setFlow("enroll")}>{totp.length > 0 ? t("account.details.mfaAddAnotherAuthenticator", "Add another authenticator") : t("account.details.setUpAuthenticator", "Set up authenticator app")}</Btn>
            </div>
            <div style={{ fontSize: T.text.label.fontSize, color: T.soft, display: "flex", gap: T.space[1], alignItems: "flex-start", marginTop: T.space[1] }}>
              <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 /* optical */ }}><Icon.info size={12} /></span>
              <span>{t("account.details.mfaNoSmsNote", "Studio doesn't offer SMS codes as a second factor. Authenticator apps and passkeys can't be intercepted the way a text message can.")}</span>
            </div>
          </div>
        )}
      </Card>

      <PasskeysCard />

      {flow === "enroll" ? <MfaEnrollDrawer onClose={() => setFlow(null)} onDone={() => { setFlow(null); onChanged(); }} /> : null}
      {flow === "codes" ? <RecoveryCodesDrawer remaining={recovery?.remainingCodes ?? null} onClose={() => setFlow(null)} /> : null}
      {removeSms ? (
        <Modal title={t("account.details.mfaRemoveSmsTitle", "Remove SMS backup?")} tone="danger" icon={<Icon.warn size={16} />}
          onClose={() => setRemoveSms(null)}
          footer={<>
            <Btn variant="secondary" onClick={() => setRemoveSms(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn danger onClick={() => { setRemoveSms(null); toast({ tone: "warning", title: t("account.details.mfaPreviewOnly", "Design preview — nothing was changed") }); }}>{t("common.actions.remove", "Remove")}</Btn>
          </>}>
          <p style={{ fontSize: T.text.body.fontSize, color: T.n2, lineHeight: 1.55, margin: 0 }}>
            {t("account.details.mfaRemoveSmsBody", "Sign-in codes will no longer be sent to your phone. Make sure your authenticator app works and your recovery codes are saved before removing this.")}
          </p>
        </Modal>
      ) : null}
    </>
  );
}

/** A passkey is a FIRST-factor sign-in method — you sign in *with* it instead of
 *  an email and password — so it deliberately does not live inside the Two-factor
 *  card. Sign-in itself happens on the identity-hosted login page, not here; this
 *  card only manages the credentials on the account.
 *
 *  Every state below is reachable at design time: the mock refuses
 *  registration-options with STEP_UP_REQUIRED until a password is supplied (the
 *  production default), and
 *  `?passkey=cancel|duplicate|unsupported|rp-mismatch|fail` drives the ceremony
 *  outcomes a real authenticator cannot be made to produce on demand — including
 *  the relying-party misconfiguration, which nobody can trigger from inside a
 *  preview and which must never be worded as a browser-capability problem. */
function PasskeysCard() {
  const t = useT();
  const [passkeys, setPasskeys] = useState<Any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [stepUpPassword, setStepUpPassword] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: "danger" | "warning"; text: string } | null>(null);
  const [renaming, setRenaming] = useState<Any | null>(null);
  const [renameLabel, setRenameLabel] = useState("");
  const [removing, setRemoving] = useState<Any | null>(null);
  const supported = isPasskeySupported();

  const load = () => {
    (listAccountPasskeys as unknown as (o: never) => Promise<Any>)(ACCT)
      .then((r: Any) => setPasskeys(r.passkeys ?? []))
      .catch(() => setPasskeys([]));
  };
  useEffect(load, []);

  const add = (currentPassword?: string) => {
    setBusy(true); setNotice(null);
    (registerPasskey as unknown as (i: Any, o: never) => Promise<Any>)(
      { ...(label.trim() ? { label: label.trim() } : {}), ...(currentPassword ? { currentPassword } : {}) },
      ACCT
    ).then((res: Any) => {
      if (res.ok) {
        setPasskeys(res.passkeys); setAdding(false); setLabel(""); setStepUpPassword(null);
        toast({ tone: "success", title: t("account.details.passkeyAdded", "Passkey added") });
        return;
      }
      // Every branch is handled explicitly: an unhandled reason would leave the
      // drawer spinning with nothing said, which is worse than any message.
      if (res.reason === "step_up_required") { setStepUpPassword(""); return; }
      if (res.reason === "cancelled") { setAdding(false); return; }
      if (res.reason === "duplicate") {
        setNotice({ tone: "warning", text: t("account.details.passkeyDuplicate", "That device already has a passkey for this account.") });
        return;
      }
      if (res.reason === "unsupported") {
        setNotice({ tone: "danger", text: t("account.details.passkeyUnsupported", "This browser cannot create passkeys.") });
        return;
      }
      if (res.reason === "relying_party_mismatch") {
        // NOT a browser-capability problem, and it must not be worded like one.
        // The browser refused because the server handed it a relying-party id
        // that is not valid for the domain this page is served from — a
        // deployment misconfiguration on our side. Telling the person their
        // browser is at fault would send them to reinstall something that works
        // fine, and it would bury the one setting that cannot be corrected after
        // the fact: credentials are permanently scoped to the relying-party id,
        // so anything enrolled under a wrong one has to be enrolled again.
        //
        // Deliberately no retry affordance — retrying cannot change the outcome.
        setNotice({
          tone: "danger",
          text: t(
            "account.details.passkeyRelyingPartyMismatch",
            "Passkeys aren't set up correctly for this site yet. This is a configuration problem on our side, not with your device — please let your administrator know.",
          ),
        });
        return;
      }
      setNotice({ tone: "danger", text: t("account.details.passkeyFailed", "Couldn't add that passkey. Try again.") });
    }).catch((e: unknown) => {
      // registerPasskey resolves every ceremony outcome as { ok, reason }, so a
      // REJECTION here is transport or an unexpected server error — never a
      // step-up or a cancelled prompt. Without this branch the drawer kept its
      // "Waiting for your device…" label forever and said nothing.
      console.warn("registerPasskey failed:", e);
      setNotice({ tone: "danger", text: t("account.details.passkeyFailed", "Couldn't add that passkey. Try again.") });
    }).finally(() => setBusy(false));
  };

  const rename = () => {
    const next = renameLabel.trim();
    if (!next || !renaming) return;
    setBusy(true);
    (renameAccountPasskey as unknown as (id: string, i: Any, o: never) => Promise<Any>)(renaming.id, { label: next }, ACCT)
      .then((r: Any) => { setPasskeys(r.passkeys); setRenaming(null); })
      .catch(() => toast({ tone: "danger", title: t("account.details.passkeyRenameFailed", "Couldn't rename that passkey") }))
      .finally(() => setBusy(false));
  };

  const remove = () => {
    if (!removing) return;
    setBusy(true);
    (deleteAccountPasskey as unknown as (id: string, o: never) => Promise<Any>)(removing.id, ACCT)
      .then((r: Any) => { setPasskeys(r.passkeys); setRemoving(null); })
      .catch((e: Any) => {
        setRemoving(null);
        // Removal is session-freshness only — it takes no password, so the only
        // recovery is re-authenticating. Never prompt for a password here.
        const stepUp = e?.status === 403;
        toast({
          tone: "danger",
          title: stepUp
            ? t("account.details.passkeyRemoveStepUp", "Sign in again to remove this passkey")
            : t("account.details.passkeyRemoveFailed", "Couldn't remove that passkey")
        });
      });
  };

  // `null` means the read is still in flight; `[]` means the account genuinely has
  // none. Collapsing the two (rows = passkeys ?? []) rendered the "No passkeys yet"
  // empty state while the list was still loading, so someone WITH a passkey opened
  // this tab and was told they had none until the response landed. An in-flight read
  // must never be presentable as an answer.
  const loading = passkeys === null;
  const rows = passkeys ?? [];

  return (
    <>
      <Card style={{ padding: T.space[6] }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: T.space[3] }}>
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("account.details.passkeys", "Passkeys")}</h3>
          {rows.length > 0 ? <Pill tone="success" dot>{t("account.details.enabled", "Enabled")}</Pill> : null}
        </div>
        <p style={tx.help}>
          {t("account.details.passkeysIntro", "Sign in with your fingerprint, face or device PIN instead of a password. A passkey cannot be phished or reused on a fake site.")}
        </p>

        {!supported ? (
          <div style={{ marginTop: T.space[4], padding: T.space[4], borderRadius: T.radius.md, background: T.n6, textAlign: "center" }}>
            <div style={{ fontSize: T.text.body.fontSize, fontWeight: 600, color: T.n1 }}>{t("account.details.passkeyUnsupportedTitle", "This browser cannot use passkeys")}</div>
            <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.5, marginTop: T.space[1] }}>
              {t("account.details.passkeyUnsupportedBody", "Try a current version of Chrome, Safari, Edge or Firefox on a device with a screen lock.")}
            </div>
          </div>
        ) : loading ? (
          <div
            style={{ marginTop: T.space[4], padding: T.space[5], borderRadius: T.radius.md, background: T.n6, textAlign: "center",
                     fontSize: T.text.bodySm.fontSize, color: T.n2 }}
            role="status"
            aria-live="polite"
          >
            {t("account.details.passkeyLoading", "Loading your passkeys…")}
          </div>
        ) : rows.length === 0 ? (
          <div style={{ marginTop: T.space[4], padding: T.space[5], borderRadius: T.radius.md, background: T.n6, textAlign: "center" }}>
            <div style={{ fontSize: T.text.body.fontSize, fontWeight: 600, color: T.n1 }}>{t("account.details.passkeyEmptyTitle", "No passkeys yet")}</div>
            <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.5, marginTop: T.space[1], maxWidth: 420, margin: `${T.space[1]} auto 0` }}>
              {t("account.details.passkeyEmptyBody", "Add one and you can sign in without typing a password. Your password still works.")}
            </div>
            <div style={{ marginTop: T.space[3] }}>
              <Btn icon={<Icon.lock size={12} />} onClick={() => setAdding(true)}>{t("account.details.passkeyAdd", "Add a passkey")}</Btn>
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginTop: T.space[4], display: "flex", flexDirection: "column", gap: T.space[2] }}>
              {rows.map((k: Any) => (
                <div key={k.id} style={{ display: "flex", alignItems: "center", gap: T.space[3], padding: T.space[3], borderRadius: T.radius.md, border: `1px solid ${T.n5}` }}>
                  <span style={{ width: 32, height: 32, borderRadius: T.radius.control, background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.lock size={14} /></span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
                      <span style={{ fontSize: T.text.body.fontSize, fontWeight: 600, color: T.n1 }}>{k.label}</span>
                      {k.backupState ? <Pill tone="neutral">{t("account.details.passkeySynced", "Synced")}</Pill> : null}
                    </div>
                    <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: 2 }}>
                      {k.lastUsedAt
                        ? t("account.details.text", "{arg1} {arg2}", { arg1: t("account.details.passkeyLastUsed", "Last used"), arg2: new Date(k.lastUsedAt).toLocaleDateString() })
                        : t("account.details.passkeyNeverUsed", "Never used")}
                      {" · "}
                      {t("account.details.text", "{arg1} {arg2}", { arg1: t("account.details.passkeyAddedOn", "Added"), arg2: new Date(k.createdAt).toLocaleDateString() })}
                    </div>
                  </div>
                  <Btn variant="secondary" size="sm" onClick={() => { setRenaming(k); setRenameLabel(k.label); }}>{t("common.actions.rename", "Rename")}</Btn>
                  <Btn variant="secondary" size="sm" danger icon={<Icon.trash size={12} />} onClick={() => setRemoving(k)}>{t("common.actions.remove", "Remove")}</Btn>
                </div>
              ))}
            </div>
            <div style={{ marginTop: T.space[3] }}>
              <Btn variant="secondary" size="sm" icon={<Icon.lock size={12} />} onClick={() => setAdding(true)}>{t("account.details.passkeyAddAnother", "Add another passkey")}</Btn>
            </div>
          </>
        )}
      </Card>

      {adding ? (
        <Drawer panel="passkey-add" title={t("account.details.passkeyAdd", "Add a passkey")}
          sub={t("account.details.passkeys", "Passkeys")} icon={<Icon.lock size={16} />} width={520}
          onClose={() => { setAdding(false); setStepUpPassword(null); setNotice(null); }}
          footer={<>
            <Btn variant="secondary" onClick={() => { setAdding(false); setStepUpPassword(null); setNotice(null); }}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn disabled={busy || (stepUpPassword !== null && !stepUpPassword)} onClick={() => add(stepUpPassword ?? undefined)}>
              {busy ? t("account.details.passkeyWaiting", "Waiting for your device…") : t("common.actions.continue", "Continue")}
            </Btn>
          </>}>
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[4] }}>
            {notice ? (
              <div role="note" style={{ padding: T.space[3], borderRadius: T.radius.md, border: `1px solid ${alpha(notice.tone === "danger" ? T.red1 : T.orange1, 55)}`, background: alpha(notice.tone === "danger" ? T.red1 : T.orange1, 12), fontSize: T.text.bodySm.fontSize, color: T.n1 }}>
                {notice.text}
              </div>
            ) : null}
            {stepUpPassword === null ? (
              <>
                <p style={{ fontSize: T.text.body.fontSize, color: T.n2, lineHeight: 1.55, margin: 0 }}>
                  {t("account.details.passkeyAddBody", "Your device will ask you to confirm with your fingerprint, face or PIN. Give it a name so you can tell it apart later.")}
                </p>
                <LabelInput label={t("account.details.passkeyName", "Name")} value={label} onChange={setLabel} placeholder={t("account.details.passkeyNamePlaceholder", "Work laptop")} />
              </>
            ) : (
              <>
                {/* Step-up is the normal path, not an error: it fires whenever the
                    session is not freshly authenticated, which for someone already
                    sitting in account settings is most of the time. */}
                <p style={{ fontSize: T.text.body.fontSize, color: T.n2, lineHeight: 1.55, margin: 0 }}>
                  {t("account.details.passkeyStepUpBody", "Confirm your password to add a passkey.")}
                </p>
                <LabelInput label={t("account.details.currentPassword", "Current password")} value={stepUpPassword} onChange={setStepUpPassword} type="password" />
              </>
            )}
          </div>
        </Drawer>
      ) : null}

      {renaming ? (
        <Modal title={t("account.details.passkeyRename", "Rename passkey")} icon={<Icon.lock size={16} />}
          onClose={() => setRenaming(null)}
          footer={<>
            <Btn variant="secondary" onClick={() => setRenaming(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn disabled={busy || !renameLabel.trim()} onClick={rename}>{busy ? t("common.actions.saving", "Saving…") : t("common.actions.save", "Save")}</Btn>
          </>}>
          <LabelInput label={t("account.details.passkeyName", "Name")} value={renameLabel} onChange={setRenameLabel} />
        </Modal>
      ) : null}

      {removing ? (
        <Modal title={t("account.details.passkeyRemoveTitle", "Remove this passkey?")} tone="danger" icon={<Icon.warn size={16} />}
          onClose={() => setRemoving(null)}
          footer={<>
            <Btn variant="secondary" onClick={() => setRemoving(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn danger disabled={busy} onClick={remove}>{busy ? t("common.actions.removing", "Removing…") : t("common.actions.remove", "Remove")}</Btn>
          </>}>
          <p style={{ fontSize: T.text.body.fontSize, color: T.n2, lineHeight: 1.55, margin: 0 }}>
            {rows.length === 1
              ? t("account.details.passkeyRemoveLastBody", "This is your only passkey. You will need your password to sign in until you add another.")
              : t("account.details.passkeyRemoveBody", "That device will no longer be able to sign in to this account.")}
          </p>
        </Modal>
      ) : null}
    </>
  );
}

/** ASVS 2.1.8 strength meter + the mandatory-requirement checklist that sits
 *  directly under the new-password field. Character-class variety is shown as
 *  advisory only — ASVS 2.1.9 forbids composition rules as gates. */
function PasswordComplexity({ minLen, value, evalRes }: { minLen: number; value: string; evalRes: ReturnType<typeof evaluatePassword> }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const { rules, classes, strength } = evalRes;
  const bands = [
    { label: t("account.details.strengthVeryWeak", "Very weak"), color: T.red1 },
    { label: t("account.details.strengthWeak", "Weak"), color: T.red1 },
    { label: t("account.details.strengthFair", "Fair"), color: T.orange1 },
    { label: t("account.details.strengthStrong", "Strong"), color: T.green1 },
    { label: t("account.details.strengthExcellent", "Excellent"), color: T.green1 },
  ];
  const band = bands[strength.score];
  const labels: Record<RuleId, string> = {
    length: t("account.details.ruleLength", `At least ${minLen} characters`),
    maxLength: t("account.details.ruleMaxLength", `No more than ${ASVS_MAX_LENGTH} characters`),
    notCommon: t("account.details.ruleNotCommon", "Not a commonly used or breached password"),
    noPersonalInfo: t("account.details.ruleNoPersonalInfo", "Doesn't contain your name or email address"),
    noRepeats: t("account.details.ruleNoRepeats", "No repeated characters or keyboard runs (aaa, 1234, qwerty)"),
    notCurrent: t("account.details.ruleNotCurrent", "Different from your current password"),
  };
  const variety = [
    { on: classes.lower, l: t("account.details.classLower", "lowercase") },
    { on: classes.upper, l: t("account.details.classUpper", "uppercase") },
    { on: classes.digit, l: t("account.details.classDigit", "number") },
    { on: classes.symbol, l: t("account.details.classSymbol", "symbol") },
  ];
  const empty = value.length === 0;
  const unmet = rules.filter((r) => !r.ok).length;
  const summary = empty ? t("account.details.complexityHelpClosed", "What makes a strong password?")
    : unmet === 0 ? t("account.details.complexityAllMet", "All requirements met")
    : unmet === 1 ? t("account.details.complexityOneUnmet", "1 requirement not met")
    : t("account.details.complexityUnmet", `${unmet} requirements not met`);
  return (
    <div aria-live="polite" style={{ display: "flex", flexDirection: "column", gap: T.space[2], padding: `${T.space[3]} ${T.space[3]}`, border: `1px solid ${T.border}`, borderRadius: T.radius.md, background: T.n6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
        <div style={{ display: "flex", gap: T.space[1], flex: 1 }}>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} style={{ height: 4, flex: 1, borderRadius: T.radius.pill, background: !empty && strength.score > i ? band.color : T.n4 }} />
          ))}
        </div>
        <span style={{ fontSize: T.text.label.fontSize, fontWeight: 700, color: empty ? T.soft : band.color, minWidth: 74, textAlign: "right" }}>
          {empty ? t("account.details.strengthPending", "\u2014") : band.label}
        </span>
      </div>
      {/* The rule list is help, not chrome (operator 2026-07-29): collapsed by
       * default so the form stays short; the toggle row still reports how many
       * requirements are outstanding, so a disabled Update button is never a
       * mystery. */}
      <Disclosure open={open} onToggle={setOpen} summary={summary}
        accessory={!empty ? (unmet > 0 ? <span style={{ display: "inline-flex", color: T.red1 }}><Icon.warn size={12} /></span> : <span style={{ display: "inline-flex", color: T.green1 }}><Icon.check size={12} /></span>) : null}>
        <div style={{ display: "flex", flexDirection: "column", gap: T.space[3], paddingTop: T.space[1] }}>
          <div style={{ fontSize: T.text.label.fontSize, color: T.soft }}>
            {t("account.details.complexityIntro", "Requirements (OWASP password guidance):")}
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: T.space[2] }}>
            {rules.map((r) => {
              const pending = empty;
              const color = pending ? T.soft : r.ok ? T.green1 : T.red1;
              return (
                <li key={r.id} style={{ display: "flex", gap: T.space[2], alignItems: "flex-start", fontSize: T.text.bodySm.fontSize, lineHeight: 1.45, color: pending || r.ok ? T.n2 : T.n1 }}>
                  <span style={{ color, display: "inline-flex", flexShrink: 0, marginTop: 1 /* optical */ }}>
                    {pending ? <span style={{ width: 12, height: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><span style={{ width: 5, height: 5, borderRadius: T.radius.pill, background: T.n4 }} /></span> : r.ok ? <Icon.check size={12} /> : <Icon.x size={12} />}
                  </span>
                  <span>{labels[r.id]}</span>
                </li>
              );
            })}
          </ul>
          <div style={{ fontSize: T.text.label.fontSize, color: T.soft, display: "flex", flexWrap: "wrap", gap: T.space[2], alignItems: "baseline" }}>
            <span>{t("account.details.complexityOptional", "Optional, adds strength:")}</span>
            {variety.map((v) => (
              <span key={v.l} style={{ padding: `${T.space[1]} ${T.space[2]}`, borderRadius: T.radius.pill, fontSize: T.text.label.fontSize, fontWeight: 600, border: `1px solid ${v.on && !empty ? alpha(T.green1, 45) : T.border}`, background: v.on && !empty ? T.green2 : T.white, color: v.on && !empty ? T.green1 : T.soft }}>{v.l}</span>
            ))}
          </div>
        </div>
      </Disclosure>
    </div>
  );
}

/* ── Two-factor enrollment ───────────────────────────────────────────────────
 * Authenticator-app (TOTP) enrollment, the four steps every credible
 * implementation uses: install → scan → verify → save recovery codes. A Drawer,
 * not a Modal, per the overlay standard: it scrolls and carries a code list.
 *
 * There is NO MFA operation in the wiring kit yet (REQ-095), so the two things
 * only a server can issue — the otpauth secret behind the QR code and the
 * recovery codes — render as marked placeholders rather than invented values.
 * CD authors no fixture data, and a scannable QR pointing at a dead secret
 * would be worse than no QR: someone would enroll a real phone against it.
 */
function StepDots({ step, total }: { step: number; total: number }) {
  const t = useT();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: T.space[2] }}>
      <div style={{ fontSize: T.text.label.fontSize, fontWeight: 700, letterSpacing: .3, textTransform: "uppercase", color: T.soft }}>
        {t("account.details.text2", "{arg1} {step} {arg3} {total}", { arg1: t("account.details.mfaStep", "Step"), step, arg3: t("account.details.mfaStepOf", "of"), total })}
      </div>
      <div style={{ display: "flex", gap: T.space[1] }}>
        {Array.from({ length: total }, (_, i) => (
          <span key={i} style={{ height: 3, flex: 1, borderRadius: T.radius.pill, background: i < step ? T.p1 : T.n4 }} />
        ))}
      </div>
    </div>
  );
}

function PreviewNote({ text }: { text: string }) {
  // ui-raw: single-line callout with a solid T.orange2 fill and no title
  // slot — Banner's title+body layout and alpha-tint background can't
  // reproduce this exact shape/fill without redesigning it; kept raw.
  return (
    <div role="note" style={{ display: "flex", gap: T.space[2], alignItems: "flex-start", padding: T.space[3], borderRadius: T.radius.md, border: `1px solid ${alpha(T.orange1, 55)}`, background: T.orange2, fontSize: T.text.label.fontSize, color: T.n2, lineHeight: 1.5 }}>
      <span style={{ color: T.orange1, display: "inline-flex", flexShrink: 0, marginTop: 1 /* optical */ }}><Icon.warn size={13} /></span>
      <span>{text}</span>
    </div>
  );
}

function CodeGrid({ saved, onSaved }: { saved?: boolean; onSaved?: (v: boolean) => void }) {
  const t = useT();
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: T.space[2], padding: T.space[3], border: `1px dashed ${T.n4}`, borderRadius: T.radius.md, background: T.n6 }}>
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} style={{ display: "flex", gap: T.space[2], alignItems: "center", fontSize: T.text.bodySm.fontSize, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: T.soft }}>
            <span style={{ fontSize: T.text.label.fontSize, color: T.n4, width: 14, textAlign: "right" }}>{i + 1}</span>
            <span>{"\u2022\u2022\u2022\u2022-\u2022\u2022\u2022\u2022"}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: T.space[2], flexWrap: "wrap" }}>
        <Btn variant="secondary" size="sm" icon={<Icon.copy size={12} />} disabled title={t("account.details.mfaCodesPendingOp", "Recovery codes are issued by the server — pending the enrollment operation.")}>{t("common.actions.copy", "Copy")}</Btn>
        <Btn variant="secondary" size="sm" icon={<Icon.download size={12} />} disabled title={t("account.details.mfaCodesPendingOp", "Recovery codes are issued by the server — pending the enrollment operation.")}>{t("common.actions.download", "Download")}</Btn>
      </div>
      {onSaved ? (
        <Checkbox checked={!!saved} onChange={(v: boolean) => onSaved(v)} label={t("account.details.mfaCodesConfirm", "I've saved my recovery codes somewhere only I can reach — not in the same place as my password.")} />
      ) : null}
    </>
  );
}

function MfaEnrollDrawer({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const t = useT();
  const [step, setStep] = useState(1);
  const [manual, setManual] = useState(false);
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState(false);
  const [saved, setSaved] = useState(false);
  const total = 4;

  const next = () => {
    if (step === 3 && code.replace(/\D/g, "").length !== 6) { setCodeErr(true); return; }
    if (step === total) { toast({ tone: "warning", title: t("account.details.mfaPreviewOnly", "Design preview — nothing was changed") }); onDone(); return; }
    setStep((s) => s + 1);
  };

  return (
    <Drawer panel="mfa-enroll" title={t("account.details.setUpAuthenticator", "Set up authenticator app")}
      sub={t("account.details.mfaEnrollSub", "Two-factor authentication")} icon={<Icon.qr size={16} />} width={520} onClose={onClose}
      footer={<>
        <Btn variant="secondary" onClick={() => (step === 1 ? onClose() : setStep((s) => s - 1))}>
          {step === 1 ? t("common.actions.cancel", "Cancel") : t("common.actions.back", "Back")}
        </Btn>
        <Btn onClick={next} disabled={step === total && !saved}>
          {step === total ? t("account.details.mfaFinish", "Finish setup") : t("common.actions.continue", "Continue")}
        </Btn>
      </>}>
      <div style={{ display: "flex", flexDirection: "column", gap: T.space[4] }}>
        <StepDots step={step} total={total} />
        <PreviewNote text={t("account.details.mfaPreviewNote", "Design preview: no authenticator is actually enrolled, and the QR code and recovery codes below are placeholders until the enrollment operation exists.")} />

        {step === 1 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
            <h4 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("account.details.mfaStep1Title", "Install an authenticator app")}</h4>
            <p style={{ fontSize: T.text.body.fontSize, color: T.n2, lineHeight: 1.6, margin: 0 }}>
              {t("account.details.mfaStep1Body", "An authenticator app generates a fresh 6-digit code every 30 seconds, on your device, without a network connection. If you already use a password manager, it probably does this too.")}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], padding: T.space[3], border: `1px solid ${T.border}`, borderRadius: T.radius.md, background: T.n6 }}>
              <div style={{ fontSize: T.text.label.fontSize, fontWeight: 700, color: T.n2 }}>{t("account.details.mfaAppsTitle", "Apps that work")}</div>
              <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.6 }}>{t("account.details.text3", "1Password · Bitwarden · Google Authenticator · Microsoft Authenticator · Aegis")}</div>
            </div>
            <div style={{ display: "flex", gap: T.space[2], alignItems: "flex-start", fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.55 }}>
              <span style={{ color: T.soft, display: "inline-flex", flexShrink: 0, marginTop: 2 /* optical */ }}><Icon.info size={13} /></span>
              <span>{t("account.details.mfaWhyNotSms", "We don't offer codes by text message. A phone number can be moved to another SIM by someone impersonating you, and the code travels through networks neither of us controls.")}</span>
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
            <h4 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("account.details.mfaStep2Title", "Scan the QR code")}</h4>
            <p style={{ fontSize: T.text.body.fontSize, color: T.n2, lineHeight: 1.6, margin: 0 }}>
              {t("account.details.mfaStep2Body", "In your authenticator app, add a new account and point the camera at this code.")}
            </p>
            <div style={{ alignSelf: "flex-start", width: 184, height: 184, border: `1px dashed ${T.n4}`, borderRadius: T.radius.md, background: T.n6, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: T.space[2], color: T.soft, textAlign: "center", padding: T.space[3] }}>
              <Icon.qr size={40} />
              <span style={{ fontSize: T.text.label.fontSize, lineHeight: 1.45 }}>{t("account.details.mfaQrPlaceholder", "The QR code appears here once the server issues the enrollment secret.")}</span>
            </div>
            <Disclosure open={manual} onToggle={setManual} tone="brand" summary={t("account.details.mfaCantScan", "Can't scan the code?")}>
              <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], padding: T.space[3], border: `1px solid ${T.border}`, borderRadius: T.radius.md }}>
                <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.55 }}>{t("account.details.mfaManualBody", "Choose \u201center a setup key\u201d in your app and type this key instead. Time-based, 30-second interval, 6 digits.")}</div>
                <div style={{ display: "flex", gap: T.space[2], alignItems: "center", flexWrap: "wrap" }}>
                  <code style={{ flex: 1, minWidth: 200, padding: T.space[3], borderRadius: T.radius.control, background: T.n6, border: `1px dashed ${T.n4}`, fontSize: T.text.bodySm.fontSize, letterSpacing: 1.5, color: T.soft, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                    {"\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022"}
                  </code>
                  <Btn variant="secondary" size="sm" icon={<Icon.copy size={12} />} disabled title={t("account.details.mfaSecretPendingOp", "The setup key is issued by the server — pending the enrollment operation.")}>{t("common.actions.copy", "Copy")}</Btn>
                </div>
              </div>
            </Disclosure>
          </div>
        ) : null}

        {step === 3 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
            <h4 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("account.details.mfaStep3Title", "Enter the 6-digit code")}</h4>
            <p style={{ fontSize: T.text.body.fontSize, color: T.n2, lineHeight: 1.6, margin: 0 }}>
              {t("account.details.mfaStep3Body", "Type the code your app shows for Events in Studio. This proves the app and your account agree on the time.")}
            </p>
            <div style={{ maxWidth: 240 }}>
              <FieldShell label={t("account.details.mfaCodeLabel", "Verification code")}
                error={codeErr ? t("account.details.mfaCodeError", "Enter all six digits.") : undefined}
                helper={t("account.details.mfaCodeHelper", "The code changes every 30 seconds.")}>
                <OtpInput value={code} onChange={(v: string) => { setCode(v); setCodeErr(false); }} length={6} error={codeErr} />
              </FieldShell>
            </div>
          </div>
        ) : null}

        {step === 4 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
            <h4 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("account.details.mfaStep4Title", "Save your recovery codes")}</h4>
            <p style={{ fontSize: T.text.body.fontSize, color: T.n2, lineHeight: 1.6, margin: 0 }}>
              {t("account.details.mfaStep4Body", "Each code signs you in once if you lose your phone. Store them in your password manager or print them — they are shown only now, and nobody can recover them for you later.")}
            </p>
            <CodeGrid saved={saved} onSaved={setSaved} />
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}

function RecoveryCodesDrawer({ remaining, onClose }: { remaining: number | null; onClose: () => void }) {
  const t = useT();
  return (
    <Drawer panel="mfa-recovery-codes" title={t("account.details.mfaRecoveryCodes", "Recovery codes")}
      sub={t("account.details.twoFactorAuthentication", "Two-factor authentication")} icon={<Icon.csv size={16} />} width={480} onClose={onClose}
      footer={<>
        <Btn variant="secondary" onClick={onClose}>{t("common.actions.close", "Close")}</Btn>
        <Btn icon={<Icon.refresh size={12} />} onClick={() => toast({ tone: "warning", title: t("account.details.mfaPreviewOnly", "Design preview — nothing was changed") })}>
          {t("account.details.mfaRegenerate", "Generate new codes")}
        </Btn>
      </>}>
      <div style={{ display: "flex", flexDirection: "column", gap: T.space[4] }}>
        <PreviewNote text={t("account.details.mfaCodesPreviewNote", "Design preview: recovery codes are issued by the server, so these are placeholders.")} />
        {remaining != null ? (
          <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n2 }}>
            <strong style={{ color: T.n1 }}>{remaining}</strong> {t("account.details.mfaCodesRemaining", "codes still unused")}
          </div>
        ) : null}
        <p style={{ fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.6, margin: 0 }}>
          {t("account.details.mfaCodesBody", "Generating new codes invalidates the old set immediately. Do it if you think someone else has seen them, or if you're running low.")}
        </p>
        <CodeGrid />
      </div>
    </Drawer>
  );
}

/* ── Active sessions ──────────────────────────────────────────────────────
 * Backed by REAL operations: revokeAccountSession (DELETE
 * /v1/account/sessions/:id) and revokeOtherAccountSessions, both returning the
 * updated list, so a revoke re-renders from the server's answer rather than a
 * local guess. Signing a device out is destructive and unrecoverable, so both
 * paths route through a Modal that names what is being ended.
 *
 * The record keeps revoked sessions with a `revokedAt` stamp rather than
 * dropping them; they stay listed, dimmed, as a short audit trail.
 */
function relativeWhen(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  try {
    const rtf = new Intl.RelativeTimeFormat(currentLocale(), { numeric: "auto" });
    if (Math.abs(mins) < 60) return rtf.format(-mins, "minute");
    const hours = Math.round(mins / 60);
    if (Math.abs(hours) < 24) return rtf.format(-hours, "hour");
    const days = Math.round(hours / 24);
    if (Math.abs(days) < 30) return rtf.format(-days, "day");
  } catch { /* fall through to an absolute date */ }
  return friendlyDateShort(d);
}

function SessionsSection({ acct, onChanged }: { acct: Any | null; onChanged: () => void }) {
  const t = useT();
  /* `null` means the account read is still in flight; a loaded account with no
   * sessions is `[]`. Collapsing the two claimed "No active sessions." and "0
   * devices are signed in" while the request was open — a confident false
   * statement that then popped to the real list. An in-flight read is never an
   * answer (same rule as the passkeys section above). */
  const loading = acct === null;
  const all: Any[] = Array.isArray(acct?.sessions) ? acct!.sessions : [];
  /* `revokedReason` (kit 174e98dd) separates a real sign-out from session
   * ROTATION. Rotation revokes its predecessor on every token refresh, so a
   * perfectly healthy continuous session leaves a trail of revoked rows — and
   * labelling each of them "Signed out" read as repeated sign-outs. Rotation
   * predecessors are folded away entirely (counted below the list, not listed).
   * The field is a free string by contract (40+ reasons server-side, more
   * coming), so an unrecognised value renders as "Revoked", never as a
   * sign-out; only "logout" is the user's own sign-out. */
  const ROTATION_REASONS = ["rotated", "reuse_detected"]; // ui-config
  const reasonOf = (s: Any) => String(s.revokedReason ?? "");
  const isRotation = (s: Any) => !!s.revokedAt && ROTATION_REASONS.includes(reasonOf(s));
  const rotatedCount = all.filter(isRotation).length;
  const listed = all.filter((s) => !isRotation(s));
  const [confirm, setConfirm] = useState<null | { kind: "one"; s: Any } | { kind: "others" }>(null);
  const [busy, setBusy] = useState(false);

  // Current first, then most recently active, revoked last.
  const sessions = [...listed].sort((a, b) => {
    if (!!a.revokedAt !== !!b.revokedAt) return a.revokedAt ? 1 : -1;
    if (!!a.current !== !!b.current) return a.current ? -1 : 1;
    return new Date(b.lastActiveAt ?? b.createdAt ?? 0).getTime() - new Date(a.lastActiveAt ?? a.createdAt ?? 0).getTime();
  });
  const active = sessions.filter((s) => !s.revokedAt);
  const otherCount = active.filter((s) => !s.current).length;

  const fail = () => toast({ tone: "danger", title: t("account.details.sessionRevokeFailed", "Couldn't sign that session out") });
  const revokeOne = (id: string) => {
    if (typeof revokeAccountSession !== "function") return;
    setBusy(true);
    (revokeAccountSession as (id: string, o: never) => Promise<unknown>)(id, ACCT)
      .then(() => { toast({ tone: "success", title: t("account.details.sessionRevoked", "Session revoked") }); onChanged(); })
      .catch((e: unknown) => { console.warn("revokeAccountSession failed:", e); fail(); })
      .finally(() => { setBusy(false); setConfirm(null); });
  };
  const revokeOthers = () => {
    if (typeof revokeOtherAccountSessions !== "function") return;
    setBusy(true);
    (revokeOtherAccountSessions as (o: never) => Promise<unknown>)(ACCT)
      .then(() => { toast({ tone: "success", title: t("account.details.signedOutOtherSessions", "Signed out other sessions") }); onChanged(); })
      .catch((e: unknown) => { console.warn("revokeOtherAccountSessions failed:", e); fail(); })
      .finally(() => { setBusy(false); setConfirm(null); });
  };

  const deviceIcon = (fam: string) => fam === "mobile" || fam === "coord_app" || fam === "tablet" ? <Icon.phone size={14} />
    : fam === "browser" ? <Icon.browser size={14} /> : <Icon.help size={14} />;

  return (
    <>
      <Card>
        {/* ui-raw: hand-rolled header bar. Card's title/description/headerActions
         * slot renders a PanelHeader, which sets only paddingBottom (for its
         * divider) — no horizontal/top padding — so it can't reproduce this
         * bar's uniform 12/16px padding without restructuring the Card; kept
         * raw. */}
        <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: T.space[4], flexWrap: "wrap" }}>
          <div>
            <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("account.details.activeSessions", "Active sessions")}</h3>
            <p style={{ ...tx.help, marginTop: T.space[1] }}>
              {loading
                ? t("account.details.sessionsCountLoading", "Checking which devices are signed in\u2026")
                : active.length === 1
                ? t("account.details.sessionsCountOne", "1 device is signed in to your account.")
                : t("account.details.text4", "{length} {arg2}", { length: active.length, arg2: t("account.details.sessionsCountMany", "devices are signed in to your account.") })}
            </p>
          </div>
          <Btn variant="secondary" size="sm" danger icon={<Icon.logout size={12} />} disabled={loading || otherCount === 0 || busy} onClick={() => setConfirm({ kind: "others" })}>
            {t("account.details.signOutAllOtherSessions", "Sign out all other sessions")}
          </Btn>
        </div>
        {/* ui-raw: rows carry multi-line meta (client / place+when / expiry)
         * plus inline pills within the title — MediaRow's title/subtitle are
         * single-line ellipsis slots, so adopting it would clip this content;
         * kept raw. */}
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }} aria-busy={loading || undefined}>
          {loading ? [0, 1, 2].map((n) => (
            <li key={`sk-${n}`} style={{ padding: `${T.space[3]} ${T.space[4]}`, borderTop: n ? `1px solid ${T.border}` : "none", display: "flex", gap: T.space[3], alignItems: "center" }}>
              <Skeleton variant="circle" width={36} />
              <div style={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: T.space[1] }}>
                <Skeleton variant="text" width={n === 1 ? "38%" : "46%"} />
                <Skeleton variant="text" width={n === 2 ? "52%" : "30%"} height={11} />
                <Skeleton variant="text" width={n === 1 ? "44%" : "60%"} height={11} />
              </div>
            </li>
          )) : sessions.length === 0 ? <li style={{ padding: T.space[5], textAlign: "center", color: T.soft, fontSize: T.text.bodySm.fontSize }}>{t("account.details.noActiveSessions", "No active sessions.")}</li> : sessions.map((s, i) => {
            const revoked = !!s.revokedAt;
            const signedOut = revoked && reasonOf(s) === "logout";
            // Line 2 = what the session runs on; line 3 = where it is and when it
            // was last seen. ipHash covers installations that don't keep raw IPs.
            const client = [s.browser, s.os].filter(Boolean).join(" · ");
            const place = [s.locationLabel, s.ipAddress ?? (s.ipHash ? `${t("account.details.sessionIpHash", "IP hash")} ${String(s.ipHash).slice(0, 8)}` : null)].filter(Boolean).join(" · ");
            const when = revoked ? `${signedOut ? t("account.details.sessionSignedOutAt", "Signed out") : t("account.details.sessionRevokedAt", "Revoked")} ${relativeWhen(s.revokedAt)}`
              : s.current ? t("account.details.activeNow", "Active now")
              : s.lastActiveAt ? `${t("account.details.mfaLastUsed", "Last used")} ${relativeWhen(s.lastActiveAt)}` : "";
            return (
              <li key={s.id} style={{ padding: `${T.space[3]} ${T.space[4]}`, borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", gap: T.space[3], alignItems: "center", opacity: revoked ? .55 : 1, flexWrap: "wrap" }}>
                <span style={{ width: 36, height: 36, borderRadius: T.radius.control, background: s.current ? T.p3 : T.n6, color: s.current ? T.p1 : T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{deviceIcon(String(s.clientFamily ?? "unknown"))}</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: T.space[2], flexWrap: "wrap" }}>
                    <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n1, fontWeight: 600 }}>{s.deviceLabel}</div>
                    {s.current ? <Pill tone="success" dot>{t("account.details.thisSession", "This session")}</Pill> : null}
                    {revoked ? <Pill tone="neutral">{signedOut ? t("account.details.sessionRevokedPill", "Signed out") : t("account.details.sessionEndedPill", "Revoked")}</Pill> : null}
                    {s.clientFamily === "coord_app" && !String(s.deviceLabel ?? "").toLowerCase().includes("coord") ? <Pill tone="primary">{t("account.details.sessionCoordApp", "Coord app")}</Pill> : null}
                  </div>
                  {client ? <div style={{ fontSize: T.text.label.fontSize, color: T.n2, marginTop: T.space[1] }}>{client}</div> : null}
                  <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[1] }}>{[place, when].filter(Boolean).join(" · ")}</div>
                  {!revoked && s.expiresAt ? (
                    <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[1] }}>{`${t("account.details.sessionExpires", "Expires")} ${friendlyDateShort(s.expiresAt)}`}</div>
                  ) : null}
                </div>
                {!s.current && !revoked ? <Btn variant="ghost" size="sm" danger disabled={busy} onClick={() => setConfirm({ kind: "one", s })}>{t("account.details.signOut", "Sign out")}</Btn> : null}
              </li>
            );
          })}
          {!loading && rotatedCount > 0 ? (
            <li style={{ padding: `${T.space[3]} ${T.space[4]}`, borderTop: `1px solid ${T.border}`, fontSize: T.text.label.fontSize, color: T.soft, display: "flex", gap: T.space[2], alignItems: "flex-start" }}>
              <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 /* optical */ }}><Icon.info size={12} /></span>
              <span>{rotatedCount === 1
                ? t("account.details.sessionsRotatedHiddenOne", "1 earlier record from an automatic security refresh isn't shown — it isn't a sign-out.")
                : t("account.details.text5", "{rotatedCount} {arg2}", { rotatedCount, arg2: t("account.details.sessionsRotatedHidden", "earlier records from automatic security refreshes aren't shown — they aren't sign-outs.") })}</span>
            </li>
          ) : null}
        </ul>
      </Card>

      {confirm ? (
        <Modal tone="danger" icon={<Icon.logout size={16} />}
          title={confirm.kind === "others"
            ? t("account.details.sessionsConfirmOthersTitle", "Sign out all other sessions?")
            : t("account.details.sessionsConfirmOneTitle", "Sign out this device?")}
          sub={confirm.kind === "one" ? String(confirm.s.deviceLabel ?? "") : undefined}
          onClose={() => setConfirm(null)}
          footer={<>
            <Btn variant="secondary" onClick={() => setConfirm(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn danger disabled={busy} onClick={() => (confirm.kind === "others" ? revokeOthers() : revokeOne(String(confirm.s.id)))}>
              {busy ? t("account.details.signingOut", "Signing out\u2026") : t("account.details.signOut", "Sign out")}
            </Btn>
          </>}>
          <p style={{ fontSize: T.text.body.fontSize, color: T.n2, lineHeight: 1.55, margin: 0 }}>
            {confirm.kind === "others"
              ? (otherCount === 1
                ? t("account.details.sessionsConfirmOthersBodyOne", "One other device will need to sign in again. This session stays open.")
                : `${otherCount} ${t("account.details.sessionsConfirmOthersBody", "other devices will need to sign in again. This session stays open.")}`)
              : t("account.details.sessionsConfirmOneBody", "Whoever is using this device will be signed out immediately and will need your credentials to get back in.")}
          </p>
          {confirm.kind === "one" && confirm.s.locationLabel ? (
            <div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft }}>{[confirm.s.locationLabel, confirm.s.ipAddress].filter(Boolean).join(" · ")}</div>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}

/* `key` is the API toggle field; labelKey/subKey are catalog keys (REQ-048)
 * resolved at render time with the shipped literal as fallback. */
const NOTIF_ROWS: { key: string; labelKey: string; l: string; subKey: string; s: string }[] = [
  { key: "eventsAboutToGoLive", labelKey: "account.details.notifEventsAboutToGoLive", l: "Events about to go live", subKey: "account.details.notifEventsAboutToGoLiveSub", s: "30 minutes before doors open" },
  { key: "capacityReached", labelKey: "account.details.notifCapacityReached", l: "Capacity reached", subKey: "account.details.notifCapacityReachedSub", s: "When any event hits 100%" },
  { key: "newWaitlistEntries", labelKey: "account.details.notifNewWaitlistEntries", l: "New waitlist entries", subKey: "account.details.notifNewWaitlistEntriesSub", s: "Real-time as guests join" },
  { key: "dailySummary", labelKey: "account.details.notifDailySummary", l: "Daily summary", subKey: "account.details.notifDailySummarySub", s: "Every weekday at 8:00 local" },
  { key: "weeklyPerformanceDigest", labelKey: "account.details.notifWeeklyPerformanceDigest", l: "Weekly performance digest", subKey: "account.details.notifWeeklyPerformanceDigestSub", s: "Mondays · across all events" },
  { key: "productUpdates", labelKey: "account.details.notifProductUpdates", l: "Product updates", subKey: "account.details.notifProductUpdatesSub", s: "What's new in Events in Studio" },
];
function PrefsSection({ acct, onSaved }: { acct: Any | null; onSaved: () => void }) {
  const t = useT();
  const prefs: Any = acct?.preferences ?? {};
  const [email, setEmail] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!acct) return;
    setEmail({ ...(prefs.notifications?.email ?? {}) });
  }, [acct]);
  const persist = (nextEmail: Record<string, boolean>) => {
    if (typeof updateAccountPreferences !== "function") return;
    (updateAccountPreferences as (i: Any, o: never) => Promise<unknown>)({ notifications: { email: nextEmail } }, ACCT)
      .then(() => onSaved()).catch((e: unknown) => console.warn("updateAccountPreferences failed:", e));
  };
  const toggle = (k: string) => { const nx = { ...email, [k]: !email[k] }; setEmail(nx); persist(nx); };
  return (
    <Card style={{ padding: T.space[6] }}>
      <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("account.details.emailMeAbout", "Email me about")}</h3>
      <div style={{ marginTop: T.space[3], display: "flex", flexDirection: "column", gap: T.space[3] }}>
        {NOTIF_ROWS.map((p) => (
          <label key={p.key} style={{ display: "flex", gap: T.space[3], alignItems: "center", padding: `${T.space[2]} 0`, cursor: "pointer" }} onClick={() => toggle(p.key)}>
            <Toggle on={!!email[p.key]} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: T.text.body.fontSize, color: T.n1, fontWeight: 500 }}>{t(p.labelKey, p.l)}</div>
              <div style={{ fontSize: T.text.label.fontSize, color: T.soft }}>{t(p.subKey, p.s)}</div>
            </div>
          </label>
        ))}
      </div>
    </Card>
  );
}

function WorkspacesSection({ acct }: { acct: Any | null }) {
  const t = useT();
  const translate = t;
  /* Same rule as SessionsSection: `null` is the read still in flight, `[]` is a
   * loaded account with no workspaces. Collapsed, this card asserted "No
   * workspaces." against every account while the request was open. */
  const loading = acct === null;
  const workspaces: Any[] = Array.isArray(acct?.workspaces) ? acct!.workspaces : [];
  /* NO "Request access" affordance — PERMANENT operator decision (2026-07-21):
   * workspace access is never requestable from here. The list below is the set
   * your identity already has access to, with your role in each, READ-ONLY from
   * the identity system (SSO / tenant membership). the workspace-access request (retired 2026-10-13) is
   * intentionally never consumed; REQ-048(F2) is resolved by this removal. */
  return (
    <Card>
      {/* ui-raw: same PanelHeader horizontal-padding mismatch as the Sessions
       * card header above; kept raw. */}
      <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("account.details.workspacesYouCanAccess", "Workspaces you can access")}</h3>
      </div>
      {/* ui-raw: MediaRow's title/subtitle render at 14px with fixed T.space
       * paddings; this row's hand-tuned 13px/12px type + inline "Current" pill
       * inside the title line would drift if adopted — kept raw. */}
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }} aria-busy={loading || undefined}>
        {loading ? [0, 1].map((n) => (
          <li key={`ws-sk-${n}`} style={{ padding: `${T.space[3]} ${T.space[4]}`, borderTop: n ? `1px solid ${T.border}` : "none", display: "flex", gap: T.space[3], alignItems: "center" }}>
            <Skeleton variant="rect" width={36} height={36} />
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: T.space[1] }}>
              <Skeleton variant="text" width={n ? "34%" : "42%"} />
              <Skeleton variant="text" width={n ? "56%" : "48%"} height={11} />
            </div>
          </li>
        )) : workspaces.length === 0 ? <li style={{ padding: T.space[5], textAlign: "center", color: T.soft, fontSize: T.text.bodySm.fontSize }}>{t("account.details.noWorkspaces", "No workspaces.")}</li> : workspaces.map((t, i) => (
          <li key={t.id} style={{ padding: `${T.space[3]} ${T.space[4]}`, borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", gap: T.space[3], alignItems: "center" }}>
            {/* Fill + ink are the FIXED pair, not `T.p1` + `T.white`. Both of those
             * invert, so the tile flipped polarity between themes: purple-with-white
             * in light became dark-ink-on-lavender in dark (p1 -> #9E90F5,
             * white -> #212633). `T.brandFill` darkens in dark precisely so the fixed
             * `T.onColor` keeps AA, and `onColor` is also the right ink for an
             * arbitrary per-workspace `brandColor`. */}
            <span style={{ width: 36, height: 36, borderRadius: T.radius.control, background: t.brandColor ?? T.brandFill, color: T.onColor, fontSize: T.text.bodySm.fontSize, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{t.initials ?? (t.name ?? "?").slice(0, 2).toUpperCase()}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
                <div style={{ fontSize: T.text.bodySm.fontSize, color: T.n1, fontWeight: 600 }}>{t.displayName || t.name}</div>
                {t.current ? <Pill tone="primary" dot>{translate("account.details.current", "Current")}</Pill> : null}
              </div>
              <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[1] }}>{[t.plan, t.eventCount != null ? translate("account.details.workspaceEventCount", { count: t.eventCount }) : null, t.role ? translate("account.details.workspaceRole", { role: t.role }) : null].filter(Boolean).join(" · ")}</div>
            </div>
            {!t.current ? <Btn variant="ghost" size="sm" onClick={() => switchWorkspace(t.id)}>{translate("account.details.open", "Open →")}</Btn> : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function DangerSection({ acct }: { acct: Any | null }) {
  const t = useT();
  const impact: Any = acct?.closeAccountImpact ?? {};
  const close = () => {
    // Real consent gate: the contract's `confirmation` field represents the
    // USER confirming — auto-sending it unprompted would fabricate consent.
    // Native confirm() is the honest interim; a typed-confirmation modal is
    // the design follow-up (REQ-048(F)).
    if (!window.confirm(t("account.details.thisPermanentlyRemovesYourAccessAcrossEveryWorkspaceEventsYouOwnWill", "This permanently removes your access across every workspace. Events you own will remain — workspace admins must reassign them first."))) return;
    (closeAccount as unknown as (i: Any, o: never) => Promise<unknown>)({ confirmation: "CLOSE" }, ACCT)
      .then(() => toast({ tone: "warn", title: t("account.details.accountClosureStarted", "Account closure started"), subtitle: t("account.details.checkYourEmailToConfirm", "Check your email to confirm.") }))
      .catch((e: unknown) => console.warn("closeAccount failed:", e));
  };
  return (
    <Card style={{ padding: T.space[6], borderColor: alpha(T.red1, 30) }}>
      <h3 style={{ ...tx.h3, fontWeight: 600, color: T.red1 }}>{t("account.details.closeAccount", "Close account")}</h3>
      <p style={tx.help}>{t("account.details.thisPermanentlyRemovesYourAccessAcrossEveryWorkspaceEventsYouOwnWill", "This permanently removes your access across every workspace. Events you own will remain — workspace admins must reassign them first.")}</p>
      <ul style={{ margin: `${T.space[3]} 0 ${T.space[4]}`, padding: `0 0 0 ${T.space[4]}`, color: T.n2, fontSize: T.text.bodySm.fontSize, lineHeight: 1.7 }}>
        <li>{impact.apiKeyCount != null ? t("account.details.closeImpactProfileCounted", "Your profile, sessions, {apiKeyCount, plural, one {# API key} other {# API keys}}, and personal preferences are deleted.", { apiKeyCount: impact.apiKeyCount }) : t("account.details.closeImpactProfile", "Your profile, sessions, API keys, and personal preferences are deleted.")}</li>
        <li>{impact.ownedEventCount != null ? t("account.details.closeImpactEventsCounted", "{count, plural, one {The # event you created remains} other {The # events you created remain}}, but ownership transfers to the workspace admin.", { count: impact.ownedEventCount }) : t("account.details.closeImpactEvents", "Events you created remain, but ownership transfers to the workspace admin.")}</li>
        <li>{impact.workspaceCount != null ? t("account.details.closeImpactRolesCounted", "Sub-account roles across {count, plural, one {# workspace} other {# workspaces}} are removed. Re-adding requires a fresh invite.", { count: impact.workspaceCount }) : t("account.details.closeImpactRoles", "Sub-account roles across your workspaces are removed. Re-adding requires a fresh invite.")}</li>
        <li>{t("account.details.closeImpactAudit", "Audit logs of your past actions are retained for {days} days, then redacted.", { days: impact.auditRetentionDays ?? 90 })}</li>
      </ul>
      <Btn variant="primary" danger icon={<Icon.trash size={14} />} onClick={close}>{t("account.details.closeMyAccount", "Close my account")}</Btn>
    </Card>
  );
}
